<script setup lang="ts">
import type { SearchResultFragment } from '#graphql-operations'
import { UPSELL_FACET_VALUE_ID } from '@layers/common/app/constants/sort'

const props = withDefaults(defineProps<{
  title?: string
  subtitle?: string
  maxItems?: number
  carouselItemClass?: string
}>(), {
  maxItems: 16,
  carouselItemClass: 'basis-[45%] sm:basis-1/3 md:basis-1/4 pl-2',
})

const emit = defineEmits<{
  (event: 'click:product', product: SearchResultFragment): void
}>()

const activeOrderStore = useActiveOrderStore()
const { ecommerce } = useAnalytics()

const { data: upsellProducts, status } = await useAsyncData(
  `upsell-products-${UPSELL_FACET_VALUE_ID}`,
  () => useGraphqlQuery('search', {
    input: {
      facetValueFilters: [{ and: UPSELL_FACET_VALUE_ID, byProduct: true }],
      groupByProduct: true,
      sort: { price: 'ASC' },
      take: 32,
      skip: 0,
    },
  }),
  {
    transform: result => result?.data?.search?.items || [],
  },
)

function handleProductClick(product: SearchResultFragment) {
  ecommerce.selectItem(product)
  emit('click:product', product)
}

const cartProductIds = computed(() => {
  return new Set((activeOrderStore.activeOrder.data?.lines || []).map(line => line.productVariant.product.id))
})

const displayedProducts = computed(() => {
  if (!upsellProducts.value)
    return []

  // filter out items that are already in the cart

  return upsellProducts.value.slice(0, props.maxItems).filter((product) => {
    return !cartProductIds.value.has(product.id)
  })
})
</script>

<template>
  <div v-if="status === 'pending'" class="space-y-4">
    <div class="space-y-2">
      <UiSkeleton class="h-5 w-48" />
      <UiSkeleton class="h-4 w-32" />
    </div>
    <div>
      <div class="grid grid-cols-2 gap-2 md:grid-cols-4 sm:grid-cols-3">
        <ProductCardSkeleton v-for="i in 4" :key="i" />
      </div>
    </div>
  </div>

  <div v-if="displayedProducts && displayedProducts.length > 0" class="space-y-4">
    <slot name="title" />

    <div class="relative">
      <UiCarousel class="w-full">
        <UiCarouselContent class="-ml-2">
          <UiCarouselItem
            v-for="product in displayedProducts"
            :key="product.id"
            :class="carouselItemClass"
          >
            <ProductCard
              :item="product"
              @click="handleProductClick(product)"
            />
          </UiCarouselItem>
        </UiCarouselContent>
        <UiCarouselPrevious class="absolute top-1/2 hidden -left-4 sm:flex -translate-y-1/2" />
        <UiCarouselNext class="absolute top-1/2 hidden -right-4 sm:flex -translate-y-1/2" />
      </UiCarousel>
    </div>
  </div>
</template>
